/**
 * Sync Manager - Handles online/offline sync with backend
 * Priority: Backend database > localStorage (offline fallback only)
 */

class SyncManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.syncQueue = [];
    this.listeners = [];
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    
    // Load pending sync queue from localStorage
    this.loadSyncQueue();
  }

  /**
   * Check if currently online
   */
  checkOnlineStatus() {
    return navigator.onLine;
  }

  /**
   * Handle online event
   */
  handleOnline() {
    this.isOnline = true;
    this.notifyListeners('online');
    this.processSyncQueue();
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    this.isOnline = false;
    this.notifyListeners('offline');
  }

  /**
   * Add listener for connection status changes
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Notify all listeners of status change
   */
  notifyListeners(status) {
    this.listeners.forEach(callback => callback(status));
  }

  /**
   * Load sync queue from localStorage
   */
  loadSyncQueue() {
    try {
      const stored = localStorage.getItem('syncQueue');
      if (stored) {
        this.syncQueue = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load sync queue:', error);
      this.syncQueue = [];
    }
  }

  /**
   * Save sync queue to localStorage
   */
  saveSyncQueue() {
    try {
      localStorage.setItem('syncQueue', JSON.stringify(this.syncQueue));
    } catch (error) {
      console.error('Failed to save sync queue:', error);
    }
  }

  /**
   * Add operation to sync queue (for offline mode)
   */
  queueOperation(operation) {
    const queueItem = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      ...operation
    };
    
    this.syncQueue.push(queueItem);
    this.saveSyncQueue();
    
  }

  /**
   * Process all pending sync operations
   */
  async processSyncQueue() {
    if (this.syncQueue.length === 0) {
      return;
    }

    
    const queue = [...this.syncQueue];
    const failed = [];

    for (const item of queue) {
      try {
        await this.executeOperation(item);
        // Remove from queue if successful
        this.syncQueue = this.syncQueue.filter(q => q.id !== item.id);
      } catch (error) {
        console.error(`Failed to sync operation:`, item, error);
        failed.push(item);
      }
    }

    this.saveSyncQueue();

    if (failed.length === 0) {
      this.notifyListeners('sync-complete');
    } else {
      console.warn(`⚠️ ${failed.length} operations failed to sync`);
    }
  }

  /**
   * Execute a queued operation
   */
  async executeOperation(operation) {
    const { type, entity, method, endpoint, data } = operation;
    
    const response = await fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Cache-Control': 'no-cache'
      },
      body: method !== 'GET' ? JSON.stringify(data) : undefined
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get sync queue status
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      pendingOperations: this.syncQueue.length,
      queue: this.syncQueue
    };
  }

  /**
   * Clear sync queue (use with caution!)
   */
  clearQueue() {
    this.syncQueue = [];
    this.saveSyncQueue();
  }
}

// Create singleton instance
const syncManager = new SyncManager();

export default syncManager;
